import React, { createContext, memo, useContext, type FC, type ReactNode } from 'react';
import {
  Platform,
  Pressable,
  View,
  type GestureResponderEvent,
  type PressableStateCallbackType,
  type ViewStyle,
} from 'react-native';

import {
  ButtonContextValue,
  ButtonIconProps,
  ButtonTextProps,
  ButtonVariant,
  ButtonVariantStyles,
} from '@/components/ui/button/types';
import { SvgIcon } from '@/components/ui/svg-icon';
import { Text } from '@/components/ui/text';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { TypographyToken } from '@/theme/typography';

const ButtonContext = createContext<ButtonContextValue | null>(null);

const useButtonContext = () => {
  const ctx = useContext(ButtonContext);
  if (!ctx) {
    throw new Error('Button subcomponents must be used within a <Button> root component.');
  }
  return ctx;
};

export type ButtonRootProps = {
  children: ReactNode;
  onPress?: (event: GestureResponderEvent) => void;
  disabled?: boolean;
  variant?: ButtonVariant;
  // TODO: Add size if needed
  // size?: ButtonSize;
  style?: ViewStyle | ViewStyle[];
  skipPressStyle?: boolean;
};

const ButtonRoot: FC<ButtonRootProps> = ({
  children,
  onPress,
  disabled = false,
  variant = 'primary',
  // size = 'md',
  skipPressStyle = false,
  style,
}) => {
  const baseStyles = useBaseStyles();
  const pressableStyle = ({ pressed }: PressableStateCallbackType) => {
    const stylesForVariant = getVariantStyles(variant);
    return [
      baseStyles.root,
      // baseStyles[size],
      stylesForVariant.root,
      disabled && baseStyles.disabled,
      pressed && !disabled && !skipPressStyle && baseStyles.pressed,
      style,
    ];
  };

  return (
    <ButtonContext.Provider value={{ disabled, variant /** size TODO: Add size if needed */ }}>
      <Pressable disabled={disabled} onPress={onPress} style={pressableStyle}>
        <View style={baseStyles.content}>{children}</View>
      </Pressable>
    </ButtonContext.Provider>
  );
};

const ButtonIcon: FC<ButtonIconProps> = ({
  position = 'left',
  size = 24,
  color,
  styleWrapper,
  ...iconProps
}) => {
  const { disabled, variant } = useButtonContext();
  const baseStyles = useBaseStyles();
  const { iconColor, disabledIconColor } = getVariantStyles(variant);

  const resolvedColor = disabled ? disabledIconColor : (color ?? iconColor);

  return (
    <View
      style={[
        baseStyles.iconWrapper,
        position === 'left' ? baseStyles.iconLeft : baseStyles.iconRight,
        styleWrapper,
      ]}>
      <SvgIcon size={size} color={resolvedColor} {...iconProps} />
    </View>
  );
};

const ButtonText: FC<ButtonTextProps> = ({
  children,
  style,
  variant: buttonTextVariant,
  ...rest
}) => {
  const { disabled, variant } = useButtonContext();
  const { labelColor, disabledLabelColor } = getVariantStyles(variant);
  const baseStyles = useBaseStyles();
  const { isTablet } = useScaling();
  const typographyKey: TypographyToken = isTablet
    ? 'Font Inter/Subtitle/Medium 14px'
    : 'Font Inter/Subtitle/Regular 12px';

  return (
    <Text
      variant={buttonTextVariant ?? typographyKey}
      style={[baseStyles.text, { color: disabled ? disabledLabelColor : labelColor }, style]}
      {...rest}>
      {children}
    </Text>
  );
};

const getVariantStyles = (variant: ButtonVariant): ButtonVariantStyles => {
  switch (variant) {
    case 'ghost':
      return {
        root: {
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
          elevation: 0,
        },
        labelColor: colors.Blue,
        disabledLabelColor: colors.Blue,
        iconColor: colors.Blue,
        disabledIconColor: colors.Blue,
      };
    case 'outline':
      return {
        root: {
          backgroundColor: 'white',
          borderWidth: 1,
          borderColor: colors.Blue,
        },
        labelColor: colors.Blue,
        disabledLabelColor: colors.Blue,
        iconColor: colors.Blue,
        disabledIconColor: colors.Blue,
      };
    case 'primary':
    default:
      return {
        root: {
          backgroundColor: colors.Blue,
        },
        labelColor: colors['0'],
        disabledLabelColor: colors['5'],
        iconColor: colors['0'],
        disabledIconColor: colors['5'],
      };
  }
};

const useBaseStyles = createStyles(({ scale }) => ({
  root: {
    borderRadius: scale(4),
    padding: scale({
      phone: 12,
      tablet: 14,
    }),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'ios'
      ? {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.4,
          shadowRadius: 2,
        }
      : {
          elevation: 2,
        }),
  },
  md: {
    minHeight: scale(32),
  },
  pressed: {
    opacity: 0.6,
  },
  disabled: {
    opacity: 0.6,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    width: scale(24),
    height: scale(24),
  },
  iconLeft: {
    marginRight: scale(4),
  },
  iconRight: {
    marginLeft: scale(4),
  },
  text: {},
  textHighlight: {
    fontWeight: '700',
  },
}));

export const Button = Object.assign(memo(ButtonRoot), {
  Icon: ButtonIcon,
  Text: ButtonText,
});

export default Button;
